import { useCallback, useEffect, useMemo } from 'react';
import { Formik, getIn, setIn } from 'formik';
import { Button } from '@mui/material';
import Card from '../../../../common/card';
import Input from '../../../../common/input';
import { Autocomplete } from '../../../../common/autocomplete';
import SelectMenu from '../../../../common/select-menu';
import Loading from '../../../../common/loading';
import MessageError from '../../../../common/message-error';
import l from '../../../../../lang';
import { CreatePayoutData, FlowType, PurposeOfPayout, RequiredField } from '../../../../../store/payouts/interfaces';
import {
  getCreatePayoutDataValueByFieldPath,
  getFieldLabel,
  payoutPurposeOptions,
  recordToSelectMenuOptions,
} from '../utils';
import { useSelector } from 'react-redux';
import { PayoutsSelectors } from '../../../../../store/payouts/selectors';
import { useCompanyInfo } from '../../../../../store/company-information';
import { invalidBankAccountCode, invalidDocumentCode } from '../../../../../utils/error-codes';
import { ReactComponent as BriefcaseIcon } from '../../../../../assets/icons/briefcase.svg';
import { operatingCountries } from '../../../../../config';
import { useDashboard } from '../../../../../store/dashboard';

interface BankAccountFormProps {
  loading: boolean;
  validating: boolean;
  fields: RequiredField[];
  bankOptions: Array<{ code: string; name: string }>;
  initialValues: Partial<CreatePayoutData>;
  backendErrorFields?: Record<string, string>;
  onBack: () => void;
  onSubmit: (values: Record<string, string | number>) => void;
}

type SelectOption = {
  value: string | null;
  label: string;
};

/**
 * Computes backend field-level error message for a specific field based on errorInfo.
 * Returns the error message string if the field has a backend error, empty string otherwise.
 */
const getBackendFieldError = (fieldPath: string, errorInfo: { code?: number; message?: string } | undefined): string => {
  if (!errorInfo) return '';

  if (errorInfo.code === invalidDocumentCode && fieldPath === 'beneficiary.document.id' && errorInfo.message) {
    return l(errorInfo.message);
  }

  if (errorInfo.code === invalidBankAccountCode && fieldPath === 'beneficiary.bank_account.account' && errorInfo.message) {
    return l(errorInfo.message);
  }

  return '';
};

const bankAccountTypeOptionsMap: Record<string, SelectOption[]> = {
  AR: [
    {
      value: 'ALIAS',
      label: 'ALIAS',
    },
    {
      value: 'CBU',
      label: 'CBU/CVU',
    },
  ],
  BO: [],
  BR: [
    {
      value: 'CHECKING',
      label: 'Checking',
    },
    {
      value: 'SAVINGS',
      label: 'Savings',
    },
  ],
  CL: [
    {
      value: 'CHECKING',
      label: 'Checking',
    },
    {
      value: 'SAVINGS',
      label: 'Savings',
    },
    {
      value: 'VISTA',
      label: 'Vista',
    },
  ],
  CO: [
    {
      value: 'CHECKING',
      label: 'Checking',
    },
    {
      value: 'SAVINGS',
      label: 'Savings',
    },
  ],
  DO: [
    {
      value: 'CHECKING',
      label: 'Checking',
    },
    {
      value: 'SAVINGS',
      label: 'Savings',
    },
  ],
  EC: [
    {
      value: 'CHECKING',
      label: 'Checking',
    },
    {
      value: 'SAVINGS',
      label: 'Savings',
    },
  ],
  CR: [],
  MX: [
    {
      value: 'CHECKING',
      label: 'Checking',
    },
    {
      value: 'SAVINGS',
      label: 'Savings',
    },
  ],
  PE: [
    {
      value: 'CHECKING',
      label: 'Checking',
    },
    {
      value: 'SAVINGS',
      label: 'Savings',
    },
    {
      value: 'MAESTRA',
      label: 'Maestra',
    },
  ],
  PY: [
    {
      value: 'CHECKING',
      label: 'Checking',
    },
    {
      value: 'SAVINGS',
      label: 'Savings',
    },
  ],
  UY: [
    {
      value: 'CHECKING',
      label: 'Checking',
    },
    {
      value: 'SAVINGS',
      label: 'Savings',
    },
  ],
};

const BankAccountForm = ({
  loading,
  validating,
  fields,
  bankOptions,
  initialValues,
  backendErrorFields,
  onBack,
  onSubmit,
}: BankAccountFormProps) => {
  const createData = useSelector(PayoutsSelectors.getCreatePayout);
  const partialValidation = useSelector(PayoutsSelectors.getPartialValidation);
  const errorInfo = partialValidation.errorInfo;
  const { documentTypesForCompany, documentTypesForNaturalPerson, getDocumentTypesForKYC } = useCompanyInfo();
  const { merchantMe } = useDashboard();

  useEffect(() => {
    const transferCountry = createData.createPayoutData.transferCountry;
    if (transferCountry && transferCountry !== 'CL') {
      getDocumentTypesForKYC(transferCountry);
    }
  }, [createData.createPayoutData.transferCountry]);

  const getDefaultValue = useCallback(
    (field: RequiredField) => {
      if (field.fieldPath === 'remitter.nationality') {
        return merchantMe?.country ?? '';
      }

      if (field.fieldPath === 'beneficiary.purpose') {
        return PurposeOfPayout.OTHER_SERVICES;
      }

      if (field.fieldPath === 'beneficiary.document.type' && createData.createPayoutData.transferCountry === 'CL') {
        return 'RUT';
      }

      return '';
    },
    [merchantMe, createData.createPayoutData.transferCountry]
  );

  const formInitialValues = useMemo(() => {
    return fields.reduce<Record<string, any>>((accumulator, field) => {
      const mappedValue = getCreatePayoutDataValueByFieldPath(initialValues, field.fieldPath);
      const nextValue = mappedValue ?? getDefaultValue(field);
      return setIn(accumulator, field.fieldPath, nextValue);
    }, {});
  }, [fields, initialValues]);

  const documentTypes = useMemo(() => {
    if (createData.createPayoutData.transferCountry === 'CL') {
      return { RUT: 'RUT' };
    }

    if (createData.createPayoutData.flowType === FlowType.B2B) {
      return documentTypesForCompany;
    }

    return documentTypesForNaturalPerson;
  }, [
    createData.createPayoutData.flowType,
    createData.createPayoutData.transferCountry,
    documentTypesForCompany,
    documentTypesForNaturalPerson,
  ]);

  const allCountriesOptions = useMemo(() => {
    return operatingCountries.reduce<Record<string, string>>((accumulator, country) => {
      accumulator[country] = l(`countryCode.${country}`);
      return accumulator;
    }, {});
  }, []);

  const bankAutocompleteOptions = useMemo(
    () => bankOptions.map(({ code, name }) => ({ label: name, value: String(code) })),
    [bankOptions]
  );

  const purposeSelectOptions = useMemo(() => recordToSelectMenuOptions(payoutPurposeOptions), []);

  const bankAccountTypeSelectOptions = useMemo(() => {
    const options = bankAccountTypeOptionsMap[createData.createPayoutData.transferCountry ?? ''] ?? ([] as SelectOption[]);
    return options.map(option => ({ value: option.value ?? '', label: option.label }));
  }, [createData.createPayoutData.transferCountry]);

  const documentTypeSelectOptions = useMemo(() => recordToSelectMenuOptions(documentTypes), [documentTypes]);

  const countrySelectOptions = useMemo(() => recordToSelectMenuOptions(allCountriesOptions), [allCountriesOptions]);

  const validate = (values: Record<string, any>) => {
    const errors: Record<string, string> = {};

    fields.forEach(field => {
      const value = getIn(values, field.fieldPath);
      const textValue = value === undefined || value === null ? '' : String(value).trim();

      if (field.required && !textValue) {
        errors[field.fieldPath] = l('form.input.required');
        return;
      }

      if (field.maxLength && textValue.length > field.maxLength) {
        errors[field.fieldPath] = l('payouts.create.error.maxLength', field.maxLength);
        return;
      }

      if (textValue && field.dataType === 'EMAIL' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(textValue)) {
        errors[field.fieldPath] = l('form.input.invalid');
      }
    });

    return errors;
  };

  return (
    <div className="payout-create__layout">
      <Card>
        <div className="payout-create__card-content">
          <h2>{l('payouts.create.accountData.bankDataTitle')}</h2>
          <br />
          <div className="transaction_detail__title_section">
            <BriefcaseIcon className="transaction_detail__title_section__icon" />
            {l('payouts.create.accountData.bankData')}
          </div>

          {partialValidation.error &&
            errorInfo &&
            errorInfo.code !== invalidDocumentCode &&
            errorInfo.code !== invalidBankAccountCode && <MessageError errorInfo={errorInfo} />}

          <Formik
            initialValues={formInitialValues}
            validate={validate}
            enableReinitialize
            onSubmit={values => {
              const normalizedValues = fields.reduce<Record<string, string | number>>((accumulator, field) => {
                const value = getIn(values, field.fieldPath);
                if (value !== undefined) {
                  accumulator[field.fieldPath] = value;
                }
                return accumulator;
              }, {});
              onSubmit(normalizedValues);
            }}
          >
            {({ values, errors, touched, handleBlur, setFieldValue, handleSubmit }) => (
              <form onSubmit={handleSubmit}>
                {(loading || validating) && (
                  <div className="payout-create__loading">
                    <Loading />
                  </div>
                )}
                {!loading &&
                  !validating &&
                  fields.map(field => {
                    const label = getFieldLabel(field);
                    const fieldValue = getIn(values, field.fieldPath) || '';
                    const clientError = errors[field.fieldPath];
                    const hasBackendError = backendErrorFields?.[field.fieldPath] !== undefined;
                    const hasCurrentBackendError = hasBackendError
                      ? backendErrorFields?.[field.fieldPath] === String(fieldValue)
                      : false;
                    const backendFieldError = getBackendFieldError(field.fieldPath, errorInfo);
                    const error =
                      clientError || backendFieldError || (hasCurrentBackendError ? l('form.input.invalid') : '');
                    const showError =
                      backendFieldError || hasCurrentBackendError ? error : getIn(touched, field.fieldPath) ? error : '';

                    if (field.fieldPath === 'beneficiary.bank_account.code') {
                      return (
                        <Autocomplete
                          key={field.fieldPath}
                          name={field.fieldPath}
                          label={label}
                          value={String(fieldValue)}
                          options={bankAutocompleteOptions}
                          disabled={bankAutocompleteOptions.length === 0}
                          error={Boolean(showError)}
                          helperText={showError || undefined}
                          onBlur={handleBlur}
                          onChange={event => setFieldValue(field.fieldPath, event.target.value || '')}
                          renderOption={(props, option) => (
                            <li {...props} className={`${props.className ?? ''} payout-create__bank-option`}>
                              <span className="payout-create__bank-option-code">{option.value}</span>
                              <span className="payout-create__bank-option-label">{option.label}</span>
                            </li>
                          )}
                        />
                      );
                    }

                    if (field.fieldPath === 'purpose' || field.fieldPath === 'beneficiary.purpose') {
                      return (
                        <SelectMenu
                          key={field.fieldPath}
                          name={field.fieldPath}
                          label={label}
                          optionsArray={purposeSelectOptions}
                          value={fieldValue as string}
                          error={showError}
                          onBlur={handleBlur}
                          onChange={event => setFieldValue(field.fieldPath, event.target.value)}
                        />
                      );
                    }

                    if (
                      field.fieldPath === 'beneficiary.document.type' &&
                      createData.createPayoutData.transferCountry &&
                      documentTypeSelectOptions.length > 0
                    ) {
                      return (
                        <SelectMenu
                          key={field.fieldPath}
                          name={field.fieldPath}
                          label={label}
                          optionsArray={documentTypeSelectOptions}
                          value={fieldValue as string}
                          error={showError}
                          onBlur={handleBlur}
                          onChange={event => setFieldValue(field.fieldPath, event.target.value)}
                        />
                      );
                    }

                    if (
                      field.fieldPath === 'beneficiary.bank_account.type' &&
                      createData.createPayoutData.transferCountry &&
                      bankAccountTypeSelectOptions.length > 0
                    ) {
                      return (
                        <SelectMenu
                          key={field.fieldPath}
                          name={field.fieldPath}
                          label={label}
                          optionsArray={bankAccountTypeSelectOptions}
                          value={fieldValue as string}
                          error={showError}
                          onBlur={handleBlur}
                          onChange={event => setFieldValue(field.fieldPath, event.target.value)}
                        />
                      );
                    }

                    if (field.fieldPath === 'remitter.nationality' && countrySelectOptions.length > 0) {
                      return (
                        <SelectMenu
                          key={field.fieldPath}
                          name={field.fieldPath}
                          label={label}
                          optionsArray={countrySelectOptions}
                          value={fieldValue as string}
                          error={showError}
                          onBlur={handleBlur}
                          onChange={event => setFieldValue(field.fieldPath, event.target.value)}
                        />
                      );
                    }

                    if (field.fieldPath === 'beneficiary.email') {
                      return (
                        <Input
                          key={field.fieldPath}
                          name={field.fieldPath}
                          label={label}
                          type="email"
                          value={fieldValue as string}
                          error={showError}
                          onBlur={handleBlur}
                          onChange={event => setFieldValue(field.fieldPath, event.target.value)}
                          onFocus={() => undefined}
                        />
                      );
                    }

                    return (
                      <Input
                        key={field.fieldPath}
                        type={field.dataType === 'NUMBER' ? 'number' : field.dataType === 'DATE' ? 'date' : 'text'}
                        name={field.fieldPath}
                        label={label}
                        value={fieldValue}
                        maxLength={field.maxLength}
                        error={showError}
                        onBlur={handleBlur}
                        onFocus={() => undefined}
                        onChange={event => setFieldValue(field.fieldPath, event.target.value)}
                      />
                    );
                  })}

                <div className="payout-create__buttons">
                  <Button
                    variant="contained"
                    size="large"
                    onClick={onBack}
                    sx={{ backgroundColor: '#E8E8E8', color: 'black', ':hover': { backgroundColor: '#D0D0D0' } }}
                  >
                    {l('common.cancel')}
                  </Button>
                  <Button variant="contained" size="large" type="submit" disabled={loading || validating}>
                    {l('common.next')}
                  </Button>
                </div>
              </form>
            )}
          </Formik>
        </div>
      </Card>
    </div>
  );
};

export default BankAccountForm;
